<template>
  <div :class="classes">
    <!-- Mode text prop : BasicText avec troncature + tooltip auto -->
    <BasicTooltip
      v-if="hasTextProp"
      :label="textValue"
      :check-truncation="true"
      position="bottom"
    >
      <BasicText
        class="cell-text"
        :class="{ focusable: hasClickHandler }"
        :label="textValue"
        :weight="weight ?? 'light'"
        :font-style="italic ? 'italic' : 'normal'"
        :size="size ?? 'body-m'"
        :color="hasClickHandler ? 'primary-600' : undefined"
        :nb-max-lines="lines"
        :tabindex="hasClickHandler ? 0 : undefined"
        :role="hasClickHandler ? 'button' : undefined"
        @click="emit('click')"
        @keydown="onKeydown"
      />
    </BasicTooltip>
    <!-- Mode slot : le composant interne gère son propre tooltip -->
    <slot v-else />
  </div>
</template>

<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue'
  import type { BasicCellProps } from './BasicCell.types'
  import BasicTooltip from '../tooltip/BasicTooltip.vue'
  import BasicText from '../text/BasicText.vue'

  const emit = defineEmits<{
    click: []
  }>()

  const instance = getCurrentInstance()

  const props = withDefaults(defineProps<BasicCellProps>(), {
    center: false,
    right: false,
    danger: false,
    readonly: false,
    deactivated: false,
    span: 2,
    lines: '4',
    hasTooltip: false,
  })

  // Détecte si un handler @click est attaché
  const hasClickHandler = computed(() => !!instance?.vnode.props?.onClick)

  // Gestion accessibilité clavier (Enter/Space)
  const onKeydown = (event: KeyboardEvent) => {
    if (!hasClickHandler.value) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      emit('click')
    }
  }

  // Vérifie si la prop text est utilisée (même si vide)
  const hasTextProp = computed(() => props.text !== undefined)

  // Convertit text en string avec placeholder "-" si vide
  const textValue = computed(() => {
    if (props.text === null || props.text === undefined || props.text === '') return '-'
    return String(props.text)
  })

  const classes = computed(() => {
    const out: Array<string> = ['elem']
    if (props.center) out.push('elem--center')
    if (props.right) out.push('elem--right')
    if (props.span) out.push(`elem--span-${props.span}`)
    if (props.danger) out.push('elem--danger')
    if (props.deactivated) out.push('elem--deactivated')
    if (props.readonly) out.push('elem--readonly')
    if (hasClickHandler.value) out.push('elem--clickable')
    // Rétrocompatibilité : extraClass
    if (props.extraClass) {
      if (Array.isArray(props.extraClass)) out.push(...props.extraClass)
      else out.push(props.extraClass)
    }
    return out
  })
</script>
